/**
 * The workspace before the session resolves, in the workspace's own frame.
 *
 * The sidebar, top bar and page container are the real primitives, so nothing
 * moves when the session lands; only what the session decides (the tenant,
 * the destinations an account may reach) is drawn as bones.
 *
 * @module components/shell/ShellSkeleton
 */

import React from "react";

import { OverviewLoading } from "@/components/overview/OverviewLoading";
import { BrandWordmark } from "@/components/shared/BrandWordmark";
import {
  AppShell,
  PageContainer,
  PageHeader,
  Panel,
  Sidebar,
  SidebarNavItem,
  SidebarSection,
  Text,
  Topbar,
} from "@/components/ui";
import { Bone, CardBones, SkeletonField, SkeletonTable } from "@/components/ui/SkeletonParts";
import { programmeAddress, ProgrammeSkeleton } from "@/router/programme-editor/ProgrammeSkeleton";
import { SURFACES } from "@/router/programme-editor/surfaces";

import { SidebarFooter } from "./SidebarFooter";

/** A navigation item: its glyph, then its label. */
function NavBone({ width }: { width: string }): React.ReactElement {
  return (
    <div aria-hidden="true" className="flex items-center gap-3 px-3 py-2">
      <Bone className="size-5" />
      <Bone className={`h-4 ${width}`} />
    </div>
  );
}

function WorkspaceSidebarSkeleton({ version }: { version: string }): React.ReactElement {
  return (
    <Sidebar
      header={
        <div className="flex flex-col gap-2">
          <BrandWordmark size="lg" className="leading-none" />
          <Bone className="h-6 w-40" />
        </div>
      }
      footer={<SidebarFooter version={version} />}
    >
      <SidebarSection>
        {["w-20", "w-24", "w-20", "w-28", "w-16"].map((width, index) => (
          <NavBone key={`nav-${String(index)}`} width={width} />
        ))}
      </SidebarSection>
    </Sidebar>
  );
}

/**
 * The programme's sidebar before its record lands. The surfaces are fixed, so
 * they are the real links; the title and workflow state are bones.
 */
export function ProgrammeSidebarSkeleton({
  base,
  version,
}: {
  base: string;
  version: string;
}): React.ReactElement {
  const groups: readonly ["primary" | "oversight", string | undefined][] = [
    ["primary", undefined],
    ["oversight", "Oversight"],
  ];
  return (
    <Sidebar
      data-testid="programme-sidebar-loading"
      header={<BrandWordmark size="lg" className="leading-none" />}
      context={
        <div className="flex flex-col gap-2">
          <SidebarNavItem to="/programmes" end label="Back to programmes" icon="arrow-left" />
          <Panel tone="inset" flush className="flex flex-col gap-2 p-3">
            <Bone className="h-5 w-full" />
            <Bone className="h-5 w-2/3" />
            <div className="mt-1 flex flex-col items-start gap-1">
              <Text as="span" size="sm" tone="muted">
                Workflow
              </Text>
              <Bone className="h-5 w-32" />
            </div>
          </Panel>
        </div>
      }
      footer={<SidebarFooter version={version} />}
    >
      {groups.map(([group, label]) => {
        const items = SURFACES.filter((surface) => surface.group === group).map((surface) => (
          <SidebarNavItem
            key={surface.key}
            to={`${base}/${surface.segment}`}
            label={surface.title}
            icon={surface.icon}
            notUnder={[]}
          />
        ));
        return (
          <SidebarSection key={group} label={label}>
            {items}
          </SidebarSection>
        );
      })}
    </Sidebar>
  );
}

function TopbarSkeleton(): React.ReactElement {
  return (
    <Topbar
      breadcrumb={<Bone className="h-4 w-56" />}
      utilities={
        <div aria-hidden="true" className="flex items-center gap-3">
          <Bone className="size-6 rounded-full" />
          <Bone className="size-8 rounded-full" />
        </div>
      }
    />
  );
}

/** A page's heading, which is known, over the description it carries. */
function HeaderOver({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={title} description={<Bone className="mt-1 h-4 w-80 max-w-full" />} />
      {children}
    </div>
  );
}

/** A list beside the record it opens. */
function MasterDetailBones(): React.ReactElement {
  return (
    <div
      aria-hidden="true"
      className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]"
    >
      <Panel flush className="flex flex-col gap-2 p-3">
        <Bone className="h-9 w-full" />
        {Array.from({ length: 6 }, (_, index) => (
          <div key={`item-${String(index)}`} className="flex flex-col gap-2 p-3">
            <Bone className="h-4 w-4/5" />
            <Bone className="h-3 w-1/3" />
          </div>
        ))}
      </Panel>
      <Panel flush className="flex min-h-80 flex-col items-center justify-center gap-3 p-6">
        <Bone className="size-8" />
        <Bone className="h-4 w-40" />
      </Panel>
    </div>
  );
}

/** A tab strip whose tabs depend on the account, as bones. */
function TabBones({ count }: { count: number }): React.ReactElement {
  return (
    <div aria-hidden="true" className="flex gap-6 overflow-hidden border-b border-base-300 pb-3">
      {Array.from({ length: count }, (_, index) => (
        <Bone key={`tab-${String(index)}`} className="h-4 w-20 shrink-0" />
      ))}
    </div>
  );
}

function TableBones({ columns }: { columns: number }): React.ReactElement {
  return (
    <Panel flush className="p-3">
      <div aria-hidden="true" className="mb-3 flex justify-between gap-3">
        <Bone className="h-9 w-56" />
        <Bone className="h-9 w-32" />
      </div>
      {/* A phone shows the collection as cards. */}
      <div className="max-sm:hidden">
        <SkeletonTable columns={columns} rows={8} />
      </div>
      <div className="sm:hidden">
        <CardBones count={3} />
      </div>
    </Panel>
  );
}

/** The content column for a workspace address, by the page that answers it. */
function PageSkeleton({ pathname }: { pathname: string }): React.ReactElement {
  const top = pathname.split("/")[1] ?? "";
  if (top === "") {
    return <OverviewLoading />;
  }
  if (top === "programmes") {
    return (
      <HeaderOver title="Programmes">
        <TableBones columns={8} />
      </HeaderOver>
    );
  }
  if (top === "portfolio") {
    return (
      <HeaderOver title="Portfolio">
        <MasterDetailBones />
      </HeaderOver>
    );
  }
  if (top === "programme-teams") {
    return (
      <HeaderOver title="Programme teams">
        <MasterDetailBones />
      </HeaderOver>
    );
  }
  if (top === "reports") {
    return (
      <HeaderOver title="Reports">
        <TabBones count={7} />
        <TableBones columns={7} />
      </HeaderOver>
    );
  }
  if (top === "admin") {
    return (
      <HeaderOver title="Users and settings">
        <TabBones count={7} />
        <TableBones columns={5} />
      </HeaderOver>
    );
  }
  return (
    <HeaderOver title="Settings">
      <Panel>
        <div aria-hidden="true" className="grid gap-4 sm:grid-cols-2">
          <SkeletonField />
          <SkeletonField />
        </div>
      </Panel>
    </HeaderOver>
  );
}

/** The content column for an address: the programme's frame, or the page's. */
export function RouteContentSkeleton({
  pathname,
  label,
  "data-testid": testId,
}: {
  pathname: string;
  /** Read, not drawn: names what is loading. */
  label: string;
  "data-testid"?: string;
}): React.ReactElement {
  if (programmeAddress(pathname)) {
    return <ProgrammeSkeleton pathname={pathname} label={label} data-testid={testId} />;
  }
  return (
    <div role="status" aria-live="polite" data-testid={testId}>
      <span className="sr-only">{label}</span>
      <PageSkeleton pathname={pathname} />
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <ShellSkeleton pathname={pathname} version={__APP_VERSION__} label="Signing in…" />
 * ```
 */
export function ShellSkeleton({
  pathname,
  version,
  label,
  "data-testid": testId,
}: {
  pathname: string;
  version: string;
  /** Read, not drawn: names what is loading. */
  label: string;
  "data-testid"?: string;
}): React.ReactElement {
  const programme = programmeAddress(pathname);
  return (
    <AppShell
      sidebar={
        programme ? (
          <ProgrammeSidebarSkeleton base={programme.base} version={version} />
        ) : (
          <WorkspaceSidebarSkeleton version={version} />
        )
      }
      topbar={<TopbarSkeleton />}
    >
      <PageContainer>
        <RouteContentSkeleton pathname={pathname} label={label} data-testid={testId} />
      </PageContainer>
    </AppShell>
  );
}
